import { ArrowPathIcon, CheckIcon, ChevronDownIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { isReviewItemResolved } from "@utils/reportVisibility";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@fronterahealth/frontera-ui-components/ui/dropdown-menu";
import { Spinner } from "@fronterahealth/frontera-ui-components/ui/spinner";

import { ApiReportSuggestionGenerationStatusChoices } from "@api/graphql/types-and-hooks";
import { useIsPeerReviewEnabled } from "@components/QaReview/hooks/useIsPeerReviewEnabled";
import { useQaScanState } from "@components/QaReview/hooks/useQaScanState";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { ReviewView, useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import {
  isWaitingOnViewer,
  qaCategoryKey,
  qaCategoryLabel,
  reviewViewLabel,
  useReviewViewer,
} from "@components/QaReview/hooks/useReviewView";

// Radio values, one per view; a category view is "qa:<key>", so the pick stays checked whatever casing comes first.
const viewValue = (view: ReviewView): string =>
  view.kind === "qa" && view.category ? `qa:${qaCategoryKey(view.category)}` : view.kind;

// Replaces Quality Review's count while its flags aren't there to count.
const qaStatusLabel = (isChecking: boolean, isQaFailed: boolean): React.ReactNode => {
  if (isChecking) {
    return (
      <span className="ml-auto flex items-center gap-1 pl-4 text-xs text-text-tertiary">
        <Spinner aria-hidden role={undefined} className="size-3" />
        Checking
      </span>
    );
  }
  if (isQaFailed) {
    return (
      <span className="ml-auto flex items-center gap-1 pl-4 text-xs text-text-error-primary">
        <ExclamationTriangleIcon className="size-3.5 text-icon-error" />
        Didn&apos;t load
      </span>
    );
  }
  return undefined;
};

const Count: React.FC<{ value: number }> = ({ value }) => (
  <span className="ml-auto pl-4 text-xs text-text-tertiary tabular-nums">{value}</span>
);

interface ViewRowProps {
  value: string;
  label: string;
  hint?: string;
  /** Replaces the count, e.g. while Quality Review is checking. */
  status?: React.ReactNode;
  count: number;
}

// A fixed RUM action name, so clicks aren't split by the count in the row's text.
const ViewRow: React.FC<ViewRowProps> = ({ value, label, hint, status, count }) => (
  <DropdownMenuRadioItem
    value={value}
    // A pick is the whole job, so close like a plain menu item.
    closeOnClick
    data-dd-action-name={`review view ${value.split(":")[0]}`}
  >
    <span className="flex flex-col">
      {label}
      {hint && <span className="text-xs text-text-tertiary">{hint}</span>}
    </span>
    {status ?? <Count value={count} />}
  </DropdownMenuRadioItem>
);

interface ProgressProps {
  resolved: number;
  total: number;
  isChecking: boolean;
  isQaFailed: boolean;
}

// The whole review, like the completion gate, so the picked view doesn't change it. Quality Review's flags
// aren't loaded while it checks or after it fails, so no percentage then.
const Progress: React.FC<ProgressProps> = ({ resolved, total, isChecking, isQaFailed }) => {
  const isKnown = !isChecking && !isQaFailed;
  const percentage = !isKnown || total === 0 ? 0 : (resolved / total) * 100;
  const label = () => {
    if (isChecking) return "Checking";
    if (isQaFailed) return "–";
    return `${percentage.toFixed(0)}% complete`;
  };
  return (
    <div className="flex flex-col gap-2 px-2 pt-1.5 pb-2.5 text-xs">
      <div className="flex items-center justify-between gap-4">
        <span className="font-semibold text-text-primary">Checkpoint progress</span>
        <span className="text-text-secondary">{label()}</span>
      </div>
      <div className="h-1 rounded-full bg-limestone-100">
        <div className="h-1 rounded-full bg-icon-success transition-all" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

/**
 * The review bar's view picker: one pick from All open, Team Review, Quality Review (or one of its
 * categories), Waiting on you, and Resolved. The list always follows report order.
 */
export const ReviewViewMenu: React.FC = () => {
  const { resolvableQaItems, rootComments, openQaItems, openComments, status, rerun, canRerun } = useReviewData();
  const { isChecking, isFailed: isQaFailed } = useQaScanState();
  const isPeerReviewEnabled = useIsPeerReviewEnabled();
  const viewer = useReviewViewer();
  const { state, dispatch } = useReviewSession();
  const { view } = state;

  const qaStatus = qaStatusLabel(isChecking, isQaFailed);

  // Keyed without casing, shown as first seen.
  const categoryCounts = new Map<string, { label: string; count: number }>();
  openQaItems.forEach((item) => {
    const label = qaCategoryLabel(item);
    const entry = categoryCounts.get(qaCategoryKey(label)) ?? { label, count: 0 };
    categoryCounts.set(qaCategoryKey(label), { ...entry, count: entry.count + 1 });
  });
  const reviewItems = [...resolvableQaItems, ...rootComments];
  const resolvedCount = reviewItems.filter(isReviewItemResolved).length;
  // Only a finished scan runs again; while it checks or after it fails, the bar shows its state instead.
  const canRunAgain = !qaStatus && status === ApiReportSuggestionGenerationStatusChoices.Completed;
  const waitingCount = rootComments.filter((root) => isWaitingOnViewer(root, viewer)).length;

  const setView = (value: string) => {
    const view: ReviewView = value.startsWith("qa:")
      ? { kind: "qa", category: categoryCounts.get(value.slice(3))?.label }
      : ({ kind: value as Exclude<ReviewView["kind"], "qa"> } as ReviewView);
    dispatch({ type: "setReviewView", view });
  };
  const categoryRows = [...categoryCounts].map(([key, { label, count }]) => (
    <ViewRow key={key} value={`qa:${key}`} label={label} count={count} />
  ));
  const isQaView = view.kind === "qa";
  // With peer review off there is one source, so its categories move up a level.
  const soloSourceRows = qaStatus ? (
    <ViewRow value="qa" label="Quality Review" status={qaStatus} count={0} />
  ) : (
    categoryRows
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            aria-label={`Show: ${reviewViewLabel(view)}`}
            data-dd-action-name="review view menu"
            className="max-w-full min-w-0 gap-1 font-medium"
          >
            {/* The review bar's first squeeze (data-truncate) truncates the name; the full name stays in the label. */}
            <span className="truncate group-data-truncate/bar:max-w-24">{reviewViewLabel(view)}</span>
            <ChevronDownIcon className="size-3.5" />
          </Button>
        }
      />
      {/* Opens up and toward the page from the bar in the bottom-right corner. */}
      <DropdownMenuContent side="top" align="end" className="min-w-72 whitespace-nowrap">
        <Progress resolved={resolvedCount} total={reviewItems.length} isChecking={isChecking} isQaFailed={isQaFailed} />
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={viewValue(view)} onValueChange={setView}>
          <DropdownMenuGroup>
            <ViewRow value="open" label="All open" count={openQaItems.length + openComments.length} />
            {isPeerReviewEnabled ? (
              <>
                <ViewRow value="peer" label="Team Review" count={openComments.length} />
                {categoryCounts.size > 0 && !qaStatus ? (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-dd-action-name="review view qa categories">
                      {/* The chevron follows, so the count and the picked mark sit inside one flexible span. */}
                      <span className="flex flex-1 items-center">
                        Quality Review
                        <Count value={openQaItems.length} />
                        {isQaView && <CheckIcon aria-label="Picked" className="ml-1.5 size-4" />}
                      </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent side="left" className="min-w-56 whitespace-nowrap">
                      <ViewRow value="qa" label="All Quality Review" count={openQaItems.length} />
                      <DropdownMenuSeparator />
                      {categoryRows}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ) : (
                  <ViewRow value="qa" label="Quality Review" status={qaStatus} count={openQaItems.length} />
                )}
                <ViewRow value="waiting" label="Waiting on you" hint="Someone else spoke last" count={waitingCount} />
              </>
            ) : (
              soloSourceRows
            )}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <ViewRow value="resolved" label="Resolved" count={resolvedCount} />
        </DropdownMenuRadioGroup>
        {canRunAgain && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!canRerun}
              onClick={rerun}
              // The pre-move name, so RUM history stays continuous.
              data-dd-action-name="Rerun Audit"
              className="text-interaction-primary"
            >
              <ArrowPathIcon className="size-4" />
              Run Quality Review again
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
